'use client'
import type { PartialMessage } from '@bufbuild/protobuf'
import type { GetPagePublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/page/v1/page_pb'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'

import { PageFooter } from '@/components/page-footer/page-footer'
import { FooterCta } from '@/components/page-footer/shared/footer-cta'
import { PageHeader } from '@/components/page-header/page-header'
import { PageBackground } from '@/components/public-page/page-background'
import { PageFont } from '@/components/public-page/page-font'
import { PageStyle } from '@/components/public-page/page-style'
import { PageView } from '@/components/public-page/page-view'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { PHProvider } from '@/lib/posthog/providers'

import { PageNavBar } from '../public-page/page-navbar'
import { PageWhiteShadow } from '../public-page/page-white-shadow'
import { ScaleFactor } from '../public-page/scale-factor'
import { PageMeta } from '../shared/page-meta'
import { RatingBlock } from './rating-block'

type Props = {
  siteSlug: string
  data: PartialMessage<GetPagePublicResponse>
  domain?: string
}

export const PublicReviewPage = ({ siteSlug, domain, data }: Props) => {
  const { t } = useTranslation()

  return (
    <>
      {data?.site?.customHtmlBodyStart && (
        <div
          dangerouslySetInnerHTML={{ __html: data.site.customHtmlBodyStart }}
        />
      )}
      <PageBackground content={data?.pageTheme?.content} />

      <div className="relative flex h-full flex-col">
        <PageStyle content={data?.pageTheme?.content} noScaleFactor />
        <PageFont content={data?.pageTheme?.content} />
        <ScaleFactor />

        <div className="relative z-1 w-full flex-1 font-page-body font-page-body-weight text-page-text">
          <div className="relative z-30 mx-auto w-full max-w-[calc(480px*var(--scale-factor))] lg:my-fluid-4">
            <PageHeader
              page={data?.page}
              hasVerifiedBadge={!!data?.site?.verified}
              headerSlug={data?.pageTheme?.content?.headerSlug}
              headerImageStyle={data?.pageTheme?.content?.headerImageStyle}
            />

            <div className="px-fluid-4">
              <div className="mb-fluid-4">
                <div className="ft-widget-wrapper size-full rounded-page-widget text-neutral-900">
                  <div className="bg-page-widget-background shadow-page-widget border-page-widget p-block size-full rounded-page-widget text-page-body-text backdrop-blur-page-widget">
                    <Breadcrumb>
                      <BreadcrumbList>
                        <BreadcrumbItem>
                          <BreadcrumbLink
                            href={domain ? '/' : `/${siteSlug}`}
                            className="text-page-body-text hover:text-page-body-text"
                          >
                            {data?.site?.name || siteSlug}
                          </BreadcrumbLink>
                        </BreadcrumbItem>

                        <BreadcrumbSeparator className="text-page-body-text" />

                        <BreadcrumbItem>
                          <BreadcrumbPage className="text-page-body-text">
                            {t('review')}
                          </BreadcrumbPage>
                        </BreadcrumbItem>
                      </BreadcrumbList>
                    </Breadcrumb>
                  </div>
                </div>
              </div>

              <div className="flex flex-col space-y-fluid-4">
                <RatingBlock siteSlug={siteSlug} pageSlug="index" />
              </div>
            </div>

            <div className="pt-fluid-8 px-fluid-4">
              <PageFooter
                page={data?.page}
                footerSlug={data?.pageTheme?.content?.footerSlug}
                hideFingertipBranding={data?.site?.hideFingertipBranding}
                siteSlug={siteSlug}
                domain={domain}
              >
                <div className="p-block flex justify-center pt-fluid-8!">
                  <FooterCta siteSlug={siteSlug} />
                </div>
              </PageFooter>
            </div>
          </div>

          <PageWhiteShadow />
        </div>
      </div>

      <PHProvider>
        <Suspense fallback={null}>
          <PageView
            data={{
              page_slug: 'index',
              page_id: data?.page?.id,
              site_slug: siteSlug,
              site_id: data?.site?.id,
            }}
          />
        </Suspense>
      </PHProvider>

      <PageMeta site={data?.site} />

      <PageNavBar
        siteSlug={siteSlug}
        pageSlug="index"
        domain={domain}
        data={data}
      />
    </>
  )
}
